/**
 * Collapsible action log for the current hand. Resets on new hand.
 * Always shows the count; expands to the full list when toggled.
 */
import { useState } from 'react';
import type { Action } from '@/engine/types';

export type ActionLogProps = {
  actions: ReadonlyArray<Action>;
  handNumber?: number;
};

function describe(action: Action): string {
  switch (action.type) {
    case 'fold':
      return `${action.playerId}: fold`;
    case 'check':
      return `${action.playerId}: check`;
    case 'call':
      return `${action.playerId}: call ${action.amount}`;
    case 'bet':
      return `${action.playerId}: bet ${action.amount}`;
    case 'raise':
      return `${action.playerId}: raise to ${action.to}`;
    case 'allin':
      return `${action.playerId}: all-in ${action.amount}`;
  }
}

export function ActionLog({ actions, handNumber }: ActionLogProps) {
  const [open, setOpen] = useState(false);
  const count = actions.length;
  return (
    <aside
      className="pointer-events-none absolute right-2 top-14 z-20 flex flex-col items-end gap-1"
      data-testid="action-log"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="pointer-events-auto rounded-pill bg-bg-elev border border-border px-2 py-1 text-[10px] font-mono text-muted hover:text-fg"
        aria-label="액션 로그"
        data-testid="action-log-toggle"
      >
        📋 {count}
      </button>
      {open ? (
        <ol
          className="pointer-events-auto w-44 max-h-56 overflow-y-auto bg-bg-elev border border-border rounded-md px-2 py-1 text-[10px] font-mono text-fg"
          data-testid="action-log-list"
        >
          {handNumber ? <li className="text-muted mb-1">핸드 #{handNumber}</li> : null}
          {actions.length === 0 ? (
            <li className="text-muted">(no actions yet)</li>
          ) : (
            actions.map((a, i) => (
              // Append-only hand-scoped list; index is the stable identifier.
              // biome-ignore lint/suspicious/noArrayIndexKey: stable per-hand.
              <li key={i}>{describe(a)}</li>
            ))
          )}
        </ol>
      ) : null}
    </aside>
  );
}
